<template>
  <div class="theme-transition overflow-hidden min-h-screen w-full bg-[#050509] flex flex-col" :data-theme="theme">
    <!-- 顶部导航 -->
    <div class="w-full z-10 px-6 py-4 bg-[#09090b]/95 backdrop-blur-md border-b border-white/5 flex items-center justify-between shadow-lg">
      <div class="flex items-center gap-3">
        <button
          @click="$router.push('/home')"
          class="w-9 h-9 rounded-full bg-white/5 text-gray-300 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-extrabold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
            视频文案提取
          </h1>
          <p class="text-[11px] text-gray-500 mt-0.5">
            上传视频或粘贴字幕，一键生成多版本短视频脚本与标题。
          </p>
        </div>
      </div>
      <button
        @click="toggleTheme"
        class="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
      >
        <i :class="['fas', theme === 'light' ? 'fa-sun text-orange-400' : 'fa-moon']"></i>
      </button>
    </div>

    <!-- 主内容 -->
    <div class="flex-1 flex flex-col lg:flex-row max-h-[calc(100vh-64px)]">
      <!-- 左侧：输入区 -->
      <div class="w-full lg:w-[480px] border-b lg:border-b-0 lg:border-r border-custom flex flex-col component-bg">
        <div class="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
          <div>
            <label class="text-[10px] font-bold text-gray-500 mb-2 block uppercase pl-1">AI Model</label>
            <div class="relative">
              <div
                class="w-full component-tertiary rounded-xl px-4 py-3 flex items-center justify-between border border-white/5"
              >
                <div class="flex items-center space-x-3">
                  <span class="text-2xl">🎬</span>
                  <div>
                    <div class="font-medium text-sm">Ai短视频工具</div>
                    <div class="text-[11px] text-gray-500">专为短视频脚本与带货话术优化</div>
                  </div>
                </div>
                <span class="text-[10px] text-blue-400 px-2 py-0.5 rounded-full bg-blue-500/10">Beta</span>
              </div>
            </div>
          </div>

          <div class="space-y-3">
            <label class="text-[10px] lg:text-xs font-bold text-gray-500 mb-1 block uppercase pl-1">
              视频链接输入
            </label>
            <div
              class="p-3 rounded-xl border border-dashed border-custom bg-opacity-50 hover:border-blue-500/50 transition-all"
            >
              <div class="space-y-2">
                <input
                  v-model="videoUrl"
                  type="text"
                  class="w-full component-tertiary rounded-lg px-3 py-2 text-xs lg:text-sm focus:outline-none"
                  placeholder="请输入或粘贴视频链接，例如：抖音/快手/B站视频地址"
                />
                <button
                  @click="handleExtractCopy"
                  :disabled="extracting || !videoUrl.trim()"
                  class="w-full h-9 rounded-lg text-white text-xs flex items-center justify-center gap-2 transition-all"
                  :class="[
                    extracting || !videoUrl.trim()
                      ? 'bg-blue-500/50 cursor-not-allowed opacity-70'
                      : 'bg-blue-500/90 hover:bg-blue-600'
                  ]"
                >
                  <i v-if="!extracting" class="fas fa-magic"></i>
                  <i v-else class="fas fa-spinner fa-spin"></i>
                  <span>{{ extracting ? '提取中...' : '提取文案' }}</span>
                </button>
              </div>
             <!-- <p class="text-[10px] text-gray-500 mt-2 leading-relaxed">
                当前仅为界面预览，尚未接入真实解析接口。后续可根据视频链接在后端完成抓取与转写，并将结果自动填充到下方“字幕/文本”区域。
              </p> -->
            </div>

            <div class="flex flex-col" style="height: 340px;">
              <label class="text-[10px] lg:text-xs font-bold text-gray-500 mb-1 block uppercase pl-1">
                视频文案
              </label>
              <div class="relative flex-1">
                <textarea
                  v-model="videoCopyInput"
                  class="w-full h-full component-tertiary rounded-xl p-3 resize-none text-xs lg:text-sm leading-relaxed focus:outline-none"
                  placeholder="在这里粘贴视频字幕或语音识别文本，用于提取精华文案、标题与脚本结构..."
                  maxlength="4000"
                ></textarea>
                <div class="absolute bottom-2 right-3 text-[10px] text-gray-500">
                  {{ videoCopyInput.length }}/4000
                </div>
              </div>
              <!-- 文案操作按钮组：有内容时淡入显示，单选高亮 -->
              <transition name="fade">
                <div
                  v-if="videoCopyInput.trim().length > 0"
                  class="mt-3 flex flex-wrap gap-2 text-[11px]"
                >
                  <button
                    @click="selectAction('polish')"
                    :class="[
                      'px-3 py-1.5 rounded-full border transition-all',
                      selectedAction === 'polish'
                        ? 'bg-blue-500 text-white border-blue-400 shadow'
                        : 'bg-white/5 hover:bg-blue-500/20 text-gray-300 hover:text-white border-white/10'
                    ]"
                  >
                    润色文案
                  </button>
                  <button
                    @click="selectAction('mimic')"
                    :class="[
                      'px-3 py-1.5 rounded-full border transition-all',
                      selectedAction === 'mimic'
                        ? 'bg-purple-500 text-white border-purple-400 shadow'
                        : 'bg-white/5 hover:bg-purple-500/20 text-gray-300 hover:text-white border-white/10'
                    ]"
                  >
                    文案仿写
                  </button>
                  <button
                    @click="selectAction('simplify')"
                    :class="[
                      'px-3 py-1.5 rounded-full border transition-all',
                      selectedAction === 'simplify'
                        ? 'bg-emerald-500 text-white border-emerald-400 shadow'
                        : 'bg-white/5 hover:bg-emerald-500/20 text-gray-300 hover:text-white border-white/10'
                    ]"
                  >
                    精简文案
                  </button>
                  <button
                    @click="selectAction('expand')"
                    :class="[
                      'px-3 py-1.5 rounded-full border transition-all',
                      selectedAction === 'expand'
                        ? 'bg-pink-500 text-white border-pink-400 shadow'
                        : 'bg-white/5 hover:bg-pink-500/20 text-gray-300 hover:text-white border-white/10'
                    ]"
                  >
                    扩写文案
                  </button>
                </div>
              </transition>
              <!-- 一键改写按钮 -->
              <button
                @click="handleRewrite"
                :disabled="!videoCopyInput.trim() || !selectedAction || rewriting"
                class="mt-3 w-full h-9 min-h-9 flex-none shrink-0 rounded-lg text-white text-xs font-semibold flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 transition-colors"
                :class="[
                  !videoCopyInput.trim() || !selectedAction || rewriting
                    ? 'bg-indigo-500/50 cursor-not-allowed opacity-70 shadow-none'
                    : 'bg-indigo-500 hover:bg-indigo-600 shadow-sm shadow-indigo-500/20'
                ]"
              >
                <i v-if="!rewriting" class="fas fa-pen-fancy"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                <span>
                  {{ rewriting ? '改写中...' : '一键改写文案' }}
                </span>
              </button>

              <!-- 改写后的文本框，可继续编辑 v-if="rewrittenText.trim().length > 0"-->
              <transition name="fade">
                <div  class="mt-3 flex flex-col">
                  <label class="text-[10px] lg:text-xs font-bold text-gray-500 mb-1 block uppercase pl-1">
                    改写结果
                  </label>
                  <div class="relative flex-1">
                    <textarea
                      v-model="rewrittenText"
                      class="w-full h-24 component-tertiary rounded-xl p-3 resize-none text-xs lg:text-sm leading-relaxed focus:outline-none"
                      placeholder="这里会展示一键改写后的文案，你可以继续手动修改..."
                      maxlength="4000"
                    ></textarea>
                    <div class="absolute bottom-2 right-3 text-[10px] text-gray-500">
                      {{ rewrittenText.length }}/4000
                    </div>
                  </div>
                </div>
              </transition>

              <!-- 文案转音频区域 -->
              <div
                v-if="rewrittenText.trim().length > 0"
                class="mt-4 component-tertiary rounded-xl border border-white/10 p-3 space-y-3"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 text-xs font-bold text-blue-300">
                    <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-[10px]">3</span>
                    <span>音频生成</span>
                  </div>
                  <button
                    class="px-2 py-1 text-[10px] rounded-full bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
                  >
                    声音克隆
                  </button>
                </div>

                <div class="bg-black/10 rounded-lg p-3 space-y-2">
                  <div class="flex items-center justify-between text-[11px] text-gray-300">
                    <span>选择参考音频</span>
                    <div class="flex items-center gap-2">
                      <button
                        @click="previewAudio"
                        class="px-2 py-1 rounded-full flex items-center gap-1 bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-[10px] transition-colors"
                      >
                        <i :class="['fas', isPreviewPlaying ? 'fa-pause' : 'fa-play']"></i>
                        <span>{{ isPreviewPlaying ? '暂停' : '试听' }}</span>
                      </button>
                      <button
                        @click="openAudioAssetModal"
                        class="px-2 py-1 rounded border border-white/20 bg-white/5 hover:bg-white/10 text-[10px] text-gray-200 transition-colors"
                      >
                        点击选择
                      </button>
                    </div>
                  </div>
                  <p class="text-[11px] text-emerald-400 min-h-[16px]">
                    {{ audioReferenceName || '暂无选择' }}
                  </p>
                </div>

                <div class="space-y-2 text-[11px] text-gray-300">
                  <div class="flex items-center justify-between">
                    <span>语速 (0.8 - 1.2)</span>
                    <span class="text-blue-300 font-mono">{{ audioSpeed.toFixed(2) }}</span>
                  </div>
                  <input
                    v-model.number="audioSpeed"
                    type="range"
                    min="0.8"
                    max="1.2"
                    step="0.01"
                    class="w-full"
                  />
                  <div class="flex items-center justify-between">
                    <span>情感参考音频</span>
                    <select
                      v-model="audioEmotion"
                      class="ml-2 flex-1 component-tertiary rounded-lg px-2 py-1 text-[11px] outline-none"
                    >
                      <option value="neutral">平静</option>
                      <option value="happy">高兴</option>
                      <option value="excited">激情</option>
                      <option value="sad">伤感</option>
                    </select>
                  </div>
                </div>

                <button
                  @click="handleConvertToAudio"
                  :disabled="isConvertingAudio || !rewrittenText.trim()"
                  class="mt-2 w-full h-9 rounded-lg text-white text-xs flex items-center justify-center gap-2 transition-all"
                  :class="[
                    isConvertingAudio || !rewrittenText.trim()
                      ? 'bg-sky-500/50 cursor-not-allowed opacity-70'
                      : 'bg-sky-500 hover:bg-sky-600'
                  ]"
                >
                  <i v-if="!isConvertingAudio" class="fas fa-volume-up"></i>
                  <i v-else class="fas fa-spinner fa-spin"></i>
                  <span>{{ isConvertingAudio ? '生成中...' : '文案转音频' }}</span>
                </button>

                <!-- 生成后的音频预览 -->
                <div
                  v-if="generatedAudioUrl"
                  class="mt-3 flex items-center justify-between text-[11px] text-gray-300"
                >
                  <span>生成结果试听</span>
                  <button
                    @click="previewGeneratedAudio"
                    class="px-2 py-1 rounded-full flex items-center gap-1 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-[10px] transition-colors"
                  >
                    <i :class="['fas', isGeneratedPlaying ? 'fa-pause' : 'fa-play']"></i>
                    <span>{{ isGeneratedPlaying ? '暂停' : '试听结果' }}</span>
                  </button>
                </div>
              </div>

              <!-- 视频生成区域（与音频生成同级，样式一致） -->
              <div
                v-if="rewrittenText.trim().length > 0"
                class="mt-4 component-tertiary rounded-xl border border-white/10 p-3 space-y-3"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 text-xs font-bold text-blue-300">
                    <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-[10px]">4</span>
                    <span>视频生成</span>
                  </div>
                  <button
                    class="px-2 py-1 rounded-full bg-white/5 text-[10px] text-gray-300 hover:bg-white/10 transition-colors"
                  >
                    数字人管理
                  </button>
                </div>

                <div class="bg-black/10 rounded-lg p-3 space-y-2">
                  <div class="flex items-center justify-between">
                    <div class="text-[11px] text-gray-300">选择素材视频</div>
                    <button
                      @click="openVideoAssetModal"
                      class="px-3 py-1.5 rounded border border-white/20 bg-white/5 hover:bg-white/10 text-[10px] text-gray-200 transition-colors"
                    >
                      点击选择
                    </button>
                  </div>
                  <p class="text-[11px] text-emerald-400 min-h-[16px]">
                    {{ selectedVideoName || '暂无选择' }}
                  </p>
                </div>

                <div class="mt-2">
                  <button
                    @click="handleGenerateVideo"
                    :disabled="isGeneratingVideo || !selectedVideoUrl || !generatedAudioUrl"
                    class="w-full h-9 rounded-lg text-white text-xs flex items-center justify-center gap-2 transition-all"
                    :class="[
                      isGeneratingVideo || !selectedVideoUrl || !generatedAudioUrl
                        ? 'bg-sky-500/50 cursor-not-allowed opacity-70'
                        : 'bg-sky-500 hover:bg-sky-600'
                    ]"
                  >
                    <i v-if="!isGeneratingVideo" class="fas fa-video"></i>
                    <i v-else class="fas fa-spinner fa-spin"></i>
                    <span>{{ isGeneratingVideo ? '生成中...' : '生成视频' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- <div class="space-y-2">
            <label class="text-[10px] lg:text-xs font-bold text-gray-500 mb-1 block uppercase pl-1">
              输出风格
            </label>
            <select
              v-model="videoCopyStyle"
              class="w-full component-tertiary rounded-lg px-3 py-2 text-xs outline-none cursor-pointer"
            >
              <option value="summary">简洁总结</option>
              <option value="live">直播带货话术</option>
              <option value="short">短视频标题+脚本</option>
              <option value="ad">广告文案</option>
            </select>
          </div> -->
        </div>

        <!-- <div class="p-6 border-t border-custom">
          <button
            class="w-full py-3 px-6 text-white font-bold rounded-xl flex items-center justify-center gap-2 group relative overflow-hidden"
            style="background: var(--btn-gradient); background-size: 200% 200%; box-shadow: var(--shadow-glow); border: none; animation: gradientMove 3s ease infinite;"
            disabled
          >
            <span>即将上线，敬请期待</span>
            <i class="fas fa-gem text-sm animate-pulse"></i>
          </button>
          <p class="text-[10px] text-gray-500 mt-2 text-center">
            接口准备好后，只需在此处触发请求并将结果渲染到右侧区域即可。
          </p>
        </div> -->
      </div>

      <!-- 右侧：结果展示 -->
      <div class="flex-1 flex flex-col">
        <!-- 顶部：标题 + 筛选 + 搜索 -->
        <div class="p-6 border-b border-custom space-y-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="text-xl font-extrabold tracking-tight">我的项目</h3>
            </div>
            <div class="flex items-center gap-2">
              <div class="relative">
                <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs"></i>
                <input
                  v-model="projectSearch"
                  type="text"
                  placeholder="搜索项目名称"
                  class="w-56 max-w-[55vw] component-tertiary rounded-full pl-9 pr-3 py-2 text-xs outline-none border border-white/5 focus:border-blue-500/40"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 text-[11px]">
              <button
                @click="projectTab = 'all'"
                class="px-3 py-1.5 rounded-full border transition-colors"
                :class="projectTab === 'all' ? 'bg-white/10 border-white/15 text-white' : 'bg-transparent border-white/5 text-gray-400 hover:text-white hover:bg-white/5'"
              >
                全部
              </button>
              <button
                @click="projectTab = 'generating'"
                class="px-3 py-1.5 rounded-full border transition-colors"
                :class="projectTab === 'generating' ? 'bg-white/10 border-white/15 text-white' : 'bg-transparent border-white/5 text-gray-400 hover:text-white hover:bg-white/5'"
              >
                生成中
              </button>
              <button
                @click="projectTab = 'done'"
                class="px-3 py-1.5 rounded-full border transition-colors"
                :class="projectTab === 'done' ? 'bg-white/10 border-white/15 text-white' : 'bg-transparent border-white/5 text-gray-400 hover:text-white hover:bg-white/5'"
              >
                已完成
              </button>
            </div>
          </div>
        </div>

        <!-- 内容：项目网格 -->
        <div class="flex-1 overflow-y-auto p-6 scrollbar-thin">
          <div v-if="loadingProjects" class="flex items-center justify-center py-12 text-gray-400 text-sm">
            <i class="fas fa-spinner fa-spin mr-2"></i>
            加载中...
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <!-- 新建项目 -->
            <!-- <button
              @click="createNewProject"
              class="group relative aspect-[16/9] rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-blue-500/60 hover:bg-white/7 transition-all flex items-center justify-center"
            >
              <div class="flex flex-col items-center justify-center text-gray-400 group-hover:text-white">
                <div class="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-3 group-hover:bg-blue-500/70 transition-colors">
                  <i class="fas fa-plus text-base"></i>
                </div>
                <div class="text-sm font-semibold">新建项目</div>
              </div>
            </button> -->

            <!-- 项目卡片 -->
            <button
              v-for="p in filteredProjects"
              :key="p.id"
              class="group relative aspect-[16/9] rounded-2xl overflow-hidden bg-[#0f1016] border border-white/5 hover:border-white/15 transition-all"
              @click="openProject(p)"
            >
              <div class="absolute inset-0">
                <video
                  v-if="p.shuzirenUrl"
                  :src="p.shuzirenUrl"
                  class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  muted
                  playsinline
                  preload="metadata"
                />
                <div
                  v-else
                  class="w-full h-full bg-gradient-to-br from-slate-700/30 to-slate-900/50 flex items-center justify-center"
                >
                  <div class="flex items-center gap-2 text-gray-400 text-xs">
                    <i class="fas fa-spinner fa-spin"></i>
                    <span>加载中...</span>
                  </div>
                </div>
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              </div>

              <div class="absolute top-2 right-2 flex items-center gap-2">
                <button
                  v-if="p.shuzirenUrl"
                  type="button"
                  class="w-8 h-8 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                  title="预览"
                  @click.stop="previewProjectVideo(p)"
                >
                  <i class="fas fa-play text-xs"></i>
                </button>
                <button
                  v-if="p.shuzirenUrl"
                  type="button"
                  class="w-8 h-8 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                  title="下载"
                  @click.stop="downloadProjectVideo(p)"
                >
                  <i class="fas fa-download text-xs"></i>
                </button>
                <button
                  type="button"
                  class="w-8 h-8 rounded-full bg-black/45 hover:bg-red-500/80 text-white flex items-center justify-center transition-colors"
                  title="删除"
                  @click.stop="deleteProject(p)"
                >
                  <i class="fas fa-trash text-xs"></i>
                </button>
              </div>

              <div class="absolute bottom-0 left-0 right-0 p-3">
                <div class="text-sm font-semibold text-white truncate">{{ p.title }}</div>
                <div class="mt-1 flex items-center justify-between text-[10px] text-gray-300/80">
                  <span>{{ p.time }}</span>
                  <span class="px-2 py-0.5 rounded-full bg-white/10 text-gray-200">{{ p.type }}</span>
                </div>
              </div>
            </button>
          </div>

          <div v-if="!loadingProjects && filteredProjects.length === 0" class="mt-10 text-center text-gray-500 text-sm">
            暂无项目
          </div>
        </div>
      </div>
    </div>
    <!-- 角色资产选择弹窗 -->
    <transition name="fade">
      <div
        v-if="showVideoAssetModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      >
        <div
          class="w-[90vw] max-w-3xl max-h-[80vh] rounded-2xl bg-[#111216] border border-white/10 shadow-2xl overflow-hidden flex flex-col"
        >
          <div class="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div>
              <div class="text-sm font-semibold text-white">视频素材</div>
              <div class="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                <span
                  class="px-2 py-0.5 rounded-full bg-white/5 text-xs text-white font-medium"
                >角色</span>
              </div>
            </div>
            <button
              @click="closeVideoAssetModal"
              class="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            >
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>

          <div class="flex-1 px-5 py-4 overflow-y-auto">
            <div v-if="loadingHistoryList" class="flex items-center justify-center py-12 text-gray-400 text-sm">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              加载中...
            </div>
            <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <button
                v-for="asset in videoAssets"
                :key="asset.id"
                @click="selectVideoAsset(asset)"
                class="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#181920] border border-white/5 hover:border-blue-500/60 hover:shadow-lg hover:shadow-blue-500/20 transition-all flex flex-col items-center justify-center"
              >
                <div
                  v-if="asset.isCreate"
                  class="flex flex-col items-center justify-center text-gray-400 group-hover:text-white"
                >
                  <div
                    class="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-2 group-hover:bg-blue-500/70 group-hover:text-white transition-colors"
                  >
                    <i v-if="!isUploadingCreate" class="fas fa-plus text-sm"></i>
                    <i v-else class="fas fa-spinner fa-spin text-sm"></i>
                  </div>
                  <span class="text-xs">{{ isUploadingCreate ? '上传中...' : '创建' }}</span>
                </div>
                <div v-else class="flex flex-col items-center justify-between h-full w-full py-2">
                  <div class="flex-1 w-full flex items-center justify-center overflow-hidden rounded-xl bg-black/40 min-h-0">
                    <video
                      v-if="asset.videoUrl"
                      :src="asset.videoUrl"
                      class="w-full h-full object-cover pointer-events-none"
                      preload="metadata"
                      muted
                      playsinline
                    />
                    <img
                      v-else-if="asset.coverUrl"
                      :src="asset.coverUrl"
                      class="w-full h-full object-cover pointer-events-none"
                      alt=""
                    />
                    <div
                      v-else
                      class="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white text-lg font-bold shadow-inner"
                    >
                      {{ (asset.name || '未').charAt(0) }}
                    </div>
                  </div>
                  <div class="px-2 py-1 mt-1.5 rounded-full bg-black/40 text-[11px] text-gray-100 truncate max-w-full">
                    {{ asset.name }}
                  </div>
                </div>

                <button
                  v-if="!asset.isCreate"
                  type="button"
                  @click.stop="previewVideoAsset(asset)"
                  class="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/60 hover:bg-blue-500/80 text-[10px] text-white transition-colors"
                >
                  预览
                </button>
              </button>
            </div>
          </div>
          <input
            ref="createVideoUploadInput"
            type="file"
            accept="video/*"
            class="hidden"
            @change="handleCreateVideoUpload"
          />
        </div>
      </div>
    </transition>

    <!-- 视频预览弹窗 -->
    <transition name="fade">
      <div
        v-if="showPreviewModal"
        class="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm"
        @click.self="closePreviewModal"
      >
        <div class="relative w-[90vw] max-w-2xl rounded-xl overflow-hidden bg-black shadow-2xl">
          <button
            type="button"
            @click="closePreviewModal"
            class="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white"
          >
            <i class="fas fa-times"></i>
          </button>
          <video
            v-if="previewVideoUrl && !previewIsImage"
            :src="previewVideoUrl"
            class="w-full aspect-video"
            controls
            autoplay
            playsinline
          />
          <img
            v-else-if="previewVideoUrl && previewIsImage"
            :src="previewVideoUrl"
            class="w-full max-h-[80vh] object-contain"
            alt="预览"
          />
        </div>
      </div>
    </transition>

    <!-- 参考音频选择弹窗 -->
    <transition name="fade">
      <div
        v-if="showAudioAssetModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      >
        <div
          class="w-[90vw] max-w-3xl max-h-[80vh] rounded-2xl bg-[#111216] border border-white/10 shadow-2xl overflow-hidden flex flex-col"
        >
          <div class="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div>
              <div class="text-sm font-semibold text-white">参考音频</div>
              <div class="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                <span class="px-2 py-0.5 rounded-full bg-white/5 text-xs text-white font-medium">音频</span>
              </div>
            </div>
            <button
              @click="closeAudioAssetModal"
              class="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            >
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>

          <div class="flex-1 px-5 py-4 overflow-y-auto">
            <div v-if="loadingAudioList" class="flex items-center justify-center py-12 text-gray-400 text-sm">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              加载中...
            </div>
            <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <!-- 创建参考音频（图2样式） -->
              <button
                type="button"
                @click="triggerAudioCreate"
                class="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#181920] border border-blue-500/40 hover:border-blue-500/70 hover:shadow-lg hover:shadow-blue-500/20 transition-all flex flex-col items-center justify-center"
              >
                <div class="flex flex-col items-center justify-center text-gray-200">
                  <div
                    class="w-14 h-14 rounded-full bg-blue-500/70 flex items-center justify-center mb-3 group-hover:bg-blue-500 transition-colors"
                  >
                    <i v-if="!isCreatingAudio" class="fas fa-plus text-lg"></i>
                    <i v-else class="fas fa-spinner fa-spin text-lg"></i>
                  </div>
                  <div class="text-sm font-semibold">{{ isCreatingAudio ? '上传中...' : '创建' }}</div>
                </div>
              </button>

              <button
                v-for="a in audioAssets"
                :key="a.id"
                @click="selectAudioAsset(a)"
                class="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-[#181920] border border-white/5 hover:border-blue-500/60 hover:shadow-lg hover:shadow-blue-500/20 transition-all flex flex-col items-center justify-center"
              >
                <div class="relative flex flex-col items-center justify-center h-full w-full py-3">
                  <div
                    class="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/40 to-purple-700/40 flex items-center justify-center text-white text-lg font-bold shadow-inner"
                  >
                    <i class="fas fa-music"></i>
                  </div>
                  <div class="absolute bottom-3 left-3 right-3 px-2 py-1 rounded-full bg-black/40 text-[11px] text-gray-100 truncate">
                    {{ a.name }}
                  </div>
                </div>

                <button
                  v-if="a.url"
                  type="button"
                  class="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/60 hover:bg-blue-500/80 text-[10px] text-white transition-colors"
                  @click.stop="toggleAudioAssetPreview(a)"
                >
                  <span v-if="audioAssetPlayingId === a.id && isAudioAssetPlaying">暂停</span>
                  <span v-else>试听</span>
                </button>
              </button>
            </div>
          </div>
          <input
            ref="audioCreateInput"
            type="file"
            accept="audio/*"
            class="hidden"
            @change="handleAudioCreated"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '../utils/api'
import { showSuccessToast, showErrorToast } from '../utils/toast'
import { pickVideoFileUploadUrl } from '../utils/nodeAudioTts'

export default {
  name: 'VideoCopy',
  data() {
    return {
      theme: localStorage.getItem('theme') || 'dark',
      videoUrl: '',
      videoCopyInput: '',
      rewrittenText: '',
      videoCopyStyle: 'summary',
      extracting: false,
      selectedAction: '', // 当前选中的文案操作
      rewriting: false,

      // 音频生成相关（默认选中你提供的抖音示例音频）
      audioReference: 'https://sf6-cdn-tos.douyinstatic.com/obj/ies-music/7617560274899454762.mp3',
      audioReferenceName: '抖音示例音色（女声）',
      audioSpeed: 1.0,
      audioEmotion: 'neutral',
      isConvertingAudio: false,
      isPreviewPlaying: false,
      previewAudioInstance: null,
      generatedAudioUrl: '',
      isGeneratedPlaying: false,
      generatedAudioInstance: null,
      generatedVoiceId: '',
      selectedVideoName: '',
      selectedVideoUrl: '',
      showVideoAssetModal: false,
      showAudioAssetModal: false,
      isUploadingCreate: false,
      loadingHistoryList: false,
      loadingAudioList: false,
      isGeneratingVideo: false,
      generatedVideoUrl: '',
      previewVideoUrl: '',
      previewIsImage: false,
      showPreviewModal: false,
      // 右侧：我的项目
      projectTab: 'all',
      projectSearch: '',
      loadingProjects: false,
      deletingProjects: false,
      projects: [],
      audioAssets: [],
      isCreatingAudio: false,
      audioAssetPlayingId: null,
      isAudioAssetPlaying: false,
      audioAssetPreviewInstance: null,
      videoAssets: [{ id: 'create', name: '创建', isCreate: true }]
    }
  },
  computed: {
    filteredProjects() {
      const kw = (this.projectSearch || '').trim().toLowerCase()
      return this.projects
        .filter(p => (kw ? (p.title || '').toLowerCase().includes(kw) : true))
    }
  },
  watch: {
    projectTab() {
      this.fetchProjectList()
    }
  },
  mounted() {
    document.body.setAttribute('data-theme', this.theme)
    this.fetchProjectList()
  },
  methods: {
    toggleTheme() {
      this.theme = this.theme === 'light' ? 'dark' : 'light'
      localStorage.setItem('theme', this.theme)
      document.body.setAttribute('data-theme', this.theme)
    },

    async handleExtractCopy() {
      if (this.extracting) return
      const url = this.videoUrl.trim()
      if (!url) {
        showErrorToast('请输入视频链接')
        return
      }

      this.extracting = true
      try {
        const res = await api.post('/getwenan', {
          content: url
        })

        if (res && res.msg === 'success' && res.data && res.data.text) {
          this.videoCopyInput = res.data.text
          this.rewrittenText = ''
          showSuccessToast('文案提取成功')
        } else {
          const msg = (res && (res.error || res.msg)) || '文案提取失败，请稍后重试'
          showErrorToast(msg)
        }
      } catch (error) {
        console.error('提取文案失败:', error)
        showErrorToast(error.message || '文案提取失败，请检查网络或链接是否有效')
      } finally {
        this.extracting = false
      }
    },

    selectAction(action) {
      this.selectedAction = action
    },

    async handleRewrite() {
      if (this.rewriting) return
      const text = this.videoCopyInput.trim()
      if (!text) {
        showErrorToast('没有可改写的文案')
        return
      }
      if (!this.selectedAction) {
        showErrorToast('请选择改写类型')
        return
      }

      this.rewriting = true
      try {
        const res = await api.post('/changewenan', {
          content: text,
          types: this.selectedAction
        })

        if (res && res.msg === 'success' && res.data) {
          this.rewrittenText = res.data
          showSuccessToast('文案改写成功')
        } else {
          const msg = (res && (res.error || res.msg)) || '文案改写失败，请稍后重试'
          showErrorToast(msg)
        }
      } catch (error) {
        console.error('改写文案失败:', error)
        showErrorToast(error.message || '文案改写失败，请检查网络后重试')
      } finally {
        this.rewriting = false
      }
    },

    // 预览 / 暂停参考音频
    previewAudio() {
      // 如果正在播放，则暂停
      if (this.isPreviewPlaying && this.previewAudioInstance) {
        this.previewAudioInstance.pause()
        this.isPreviewPlaying = false
        return
      }

      const src =
        this.audioReference ||
        'https://sf6-cdn-tos.douyinstatic.com/obj/ies-music/7617560274899454762.mp3'
      try {
        // 如果之前有实例，先停掉
        if (this.previewAudioInstance) {
          this.previewAudioInstance.pause()
          this.previewAudioInstance = null
        }

        const audio = new Audio(src)
        this.previewAudioInstance = audio
        audio.onended = () => {
          this.isPreviewPlaying = false
          this.previewAudioInstance = null
        }

        audio
          .play()
          .then(() => {
            this.isPreviewPlaying = true
          })
          .catch((err) => {
            console.error('预览音频失败:', err)
            this.isPreviewPlaying = false
            showErrorToast('音频预览失败，请检查链接或浏览器权限')
          })
      } catch (e) {
        console.error('创建音频对象失败:', e)
        this.isPreviewPlaying = false
        showErrorToast('无法播放音频，请稍后重试')
      }
    },

    // 弹窗：选择参考音频
    openAudioAssetModal() {
      this.showAudioAssetModal = true
      this.fetchAudioHistoryList()
    },
    closeAudioAssetModal() {
      this.showAudioAssetModal = false
    },
    selectAudioAsset(a) {
      if (!a?.url) return
      this.audioReference = a.url
      this.audioReferenceName = a.name || '参考音频'
      this.closeAudioAssetModal()
      showSuccessToast('已选择参考音频：' + this.audioReferenceName)
    },
    previewAudioUrl(url) {
      try {
        const audio = new Audio(url)
        audio.play().catch((err) => {
          console.error('试听失败:', err)
          showErrorToast('试听失败，请检查链接或浏览器权限')
        })
      } catch (e) {
        console.error('创建音频对象失败:', e)
        showErrorToast('无法试听，请稍后重试')
      }
    },

    // 弹窗内音频卡片：试听/暂停（二态）
    toggleAudioAssetPreview(a) {
      const url = a?.url
      if (!url) return

      // 如果点击的是当前播放项 → 暂停
      if (this.isAudioAssetPlaying && this.audioAssetPlayingId === a.id && this.audioAssetPreviewInstance) {
        this.audioAssetPreviewInstance.pause()
        this.isAudioAssetPlaying = false
        return
      }

      // 否则：停止旧的，播放新的
      try {
        if (this.audioAssetPreviewInstance) {
          this.audioAssetPreviewInstance.pause()
          this.audioAssetPreviewInstance = null
        }

        const audio = new Audio(url)
        this.audioAssetPreviewInstance = audio
        this.audioAssetPlayingId = a.id
        audio.onended = () => {
          this.isAudioAssetPlaying = false
          this.audioAssetPreviewInstance = null
        }

        audio
          .play()
          .then(() => {
            this.isAudioAssetPlaying = true
          })
          .catch((err) => {
            console.error('试听失败:', err)
            this.isAudioAssetPlaying = false
            showErrorToast('试听失败，请检查链接或浏览器权限')
          })
      } catch (e) {
        console.error('创建音频对象失败:', e)
        this.isAudioAssetPlaying = false
        showErrorToast('无法试听，请稍后重试')
      }
    },

    // 参考音频列表来源：/history_video_list（res.data.list）
    async fetchAudioHistoryList() {
      this.loadingAudioList = true
      try {
        const res = await api.get('/history_video_list', { params: { type: 2 } })
        const raw = Array.isArray(res?.data?.list) ? res.data.list : []
        const list = raw
          .map((item, idx) => ({
            id: String(item.id ?? item.audio_id ?? item.voice_id ?? item.video_id ?? idx),
            name: item.name ?? item.title ?? item.audio_name ?? item.video_name ?? '未命名',
            url: item.audio_url ?? item.voice_url ?? item.url ?? item.src ?? ''
          }))
          .filter(a => a.url)
        this.audioAssets = list
      } catch (error) {
        console.error('获取参考音频列表失败:', error)
        this.audioAssets = []
        showErrorToast(error?.response?.data?.message || error?.message || '加载参考音频失败')
      } finally {
        this.loadingAudioList = false
      }
    },

    // 参考音频：创建（本地上传/选择）
    triggerAudioCreate() {
      if (this.isCreatingAudio) return
      const input = this.$refs.audioCreateInput
      if (input) input.click()
    },
    handleAudioCreated(event) {
      const file = event?.target?.files?.[0]
      event.target.value = ''
      if (!file) return
      if (!file.type.startsWith('audio/')) {
        showErrorToast('请选择音频文件')
        return
      }
      this.isCreatingAudio = true
      ;(async () => {
        try {
          const formData = new FormData()
          formData.append('file', file)
          // 按你的要求：type=2 + 文件名称
          formData.append('type', '2')
          formData.append('filename', file.name)
          // 兼容可能的字段名
          formData.append('file_name', file.name)
          formData.append('name', file.name)

          const res = await api.post('/video_file', formData)
          const url = pickVideoFileUploadUrl(res)
          const inner = res && typeof res === 'object' && res.data != null && typeof res.data === 'object' ? res.data : res
          const id =
            (inner && inner.id) ||
            (inner && inner.audio_id) ||
            (inner && inner.voice_id) ||
            Date.now()
          const name = (inner && inner.name) || (inner && inner.title) || file.name

          if (!url) {
            const msg = res?.message || res?.msg || res?.error || '音频上传失败'
            showErrorToast(msg)
            return
          }

          const item = { id: String(id), name, url }
          this.audioAssets = [item, ...this.audioAssets]
          this.audioReference = item.url
          this.audioReferenceName = item.name
          showSuccessToast('音频上传成功：' + item.name)
        } catch (error) {
          console.error('音频上传失败:', error)
          const msg =
            error?.response?.data?.message ||
            error?.response?.data?.msg ||
            error?.response?.data?.error ||
            error?.message ||
            '音频上传失败'
          showErrorToast(msg)
        } finally {
          this.isCreatingAudio = false
        }
      })()
    },

    // 文案转音频（异步任务两段式）：
    // 1) /yinpin_copy 获取 voice_id
    // 2) /yinpin_hecheng 传 voice_id + 文案，返回 audio_url
    async handleConvertToAudio() {
      if (this.isConvertingAudio) return
      const text = this.rewrittenText.trim()
      if (!text) {
        showErrorToast('请先完成文案改写')
        return
      }

      this.isConvertingAudio = true
      try {
        // 开始新的生成：清理上一次的试听
        this.generatedAudioUrl = ''
        this.generatedVoiceId = ''
        if (this.isGeneratedPlaying && this.generatedAudioInstance) {
          this.generatedAudioInstance.pause()
          this.isGeneratedPlaying = false
          this.generatedAudioInstance = null
        }

        // ① 获取 voice_id
        const copyRes = await api.post('/yinpin_copy', {
          audio_url: this.audioReference
        })
        const voiceId = copyRes?.data?.voice_id || copyRes?.voice_id
        if (!voiceId) {
          const msg =
            copyRes?.error ||
            copyRes?.msg ||
            copyRes?.message ||
            copyRes?.data?.message ||
            '音色初始化失败，请稍后重试'
          showErrorToast(msg)
          return
        }
        this.generatedVoiceId = voiceId

        // ② 合成：voice_id + 改写后的文案
        const hechengRes = await api.post('/yinpin_hecheng', {
          voice_id: voiceId,
          content: text,
          text
        })
        const audioUrl = hechengRes?.data?.audio_url || hechengRes?.audio_url
        if (audioUrl) {
          this.generatedAudioUrl = audioUrl
          showSuccessToast('文案转音频成功')
        } else {
          const msg =
            hechengRes?.error ||
            hechengRes?.msg ||
            hechengRes?.message ||
            hechengRes?.data?.message ||
            '音频合成失败，请稍后重试'
          showErrorToast(msg)
        }
      } catch (error) {
        console.error('文案转音频失败:', error)
        const serverMsg =
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          error?.response?.data
        const status = error?.response?.status
        if (status) {
          showErrorToast(`文案转音频失败(${status})：${serverMsg || error.message || '服务器错误'}`)
        } else {
          showErrorToast(error?.message || '文案转音频失败，请稍后重试')
        }
      } finally {
        this.isConvertingAudio = false
      }
    },

    // 预览 / 暂停生成后的音频
    previewGeneratedAudio() {
      if (!this.generatedAudioUrl) {
        showErrorToast('暂无可试听的生成音频')
        return
      }

      // 如果正在播放，则暂停
      if (this.isGeneratedPlaying && this.generatedAudioInstance) {
        this.generatedAudioInstance.pause()
        this.isGeneratedPlaying = false
        return
      }

      try {
        // 如果之前有实例，先停掉
        if (this.generatedAudioInstance) {
          this.generatedAudioInstance.pause()
          this.generatedAudioInstance = null
        }

        const audio = new Audio(this.generatedAudioUrl)
        this.generatedAudioInstance = audio
        audio.onended = () => {
          this.isGeneratedPlaying = false
          this.generatedAudioInstance = null
        }

        audio
          .play()
          .then(() => {
            this.isGeneratedPlaying = true
          })
          .catch((err) => {
            console.error('生成音频预览失败:', err)
            this.isGeneratedPlaying = false
            showErrorToast('生成音频预览失败，请检查网络或浏览器权限')
          })
      } catch (e) {
        console.error('创建生成音频对象失败:', e)
        this.isGeneratedPlaying = false
        showErrorToast('无法播放生成音频，请稍后重试')
      }
    },

    // 打开视频资产弹窗并拉取我的资产列表
    openVideoAssetModal() {
      this.showVideoAssetModal = true
      this.fetchHistoryVideoList()
    },

    // 从接口获取我的资产列表 /history_video_list
    async fetchHistoryVideoList() {
      this.loadingHistoryList = true
      try {
        const res = await api.get('/history_video_list', { params: { type: 1 } })
        const raw = Array.isArray(res?.data?.list) ? res.data.list : Array.isArray(res?.data) ? res.data : Array.isArray(res?.list) ? res.list : Array.isArray(res) ? res : []
        const list = raw.map(item => ({
          id: String(item.id ?? item.video_id ?? item.videoId ?? ''),
          name: item.name ?? item.title ?? item.video_name ?? '未命名',
          videoUrl: item.video_url ?? item.url ?? item.src ?? '',
          coverUrl: item.cover_url ?? item.cover ?? item.thumb_url ?? item.thumbnail ?? item.poster ?? ''
        })).filter(item => item.id)
        const createCard = { id: 'create', name: '创建', isCreate: true }
        this.videoAssets = [createCard, ...list]
      } catch (error) {
        console.error('获取资产列表失败:', error)
        const createCard = { id: 'create', name: '创建', isCreate: true }
        this.videoAssets = [createCard]
        showErrorToast(error?.response?.data?.message || error?.message || '加载资产列表失败')
      } finally {
        this.loadingHistoryList = false
      }
    },

    // 关闭视频资产弹窗
    closeVideoAssetModal() {
      this.showVideoAssetModal = false
    },

    // 预览资产视频（右上角「预览」）
    previewVideoAsset(asset) {
      const videoUrl = asset?.videoUrl
      const coverUrl = asset?.coverUrl
      const url = videoUrl || coverUrl
      if (!url) {
        showErrorToast('该资源暂无预览地址')
        return
      }
      this.previewVideoUrl = url
      this.previewIsImage = !videoUrl && !!coverUrl
      this.showPreviewModal = true
    },
    closePreviewModal() {
      this.showPreviewModal = false
      this.previewVideoUrl = ''
      this.previewIsImage = false
    },

    // 右侧：项目交互（当前仅占位）
    createNewProject() {
      showSuccessToast('新建项目功能占位')
    },
    openProject(p) {
      showSuccessToast('打开项目：' + (p?.title || ''))
    },
    async deleteProject(p) {
      const uuid = String(p?.uuid ?? p?.id ?? '')
      if (!uuid) return
      const ok = window.confirm(`确定删除「${p?.title || '该项目'}」吗？`)
      if (!ok) return

      this.deletingProjects = true
      try {
        const res = await api.post('/video_destroy', { uuid })
        if (res?.msg === 'success' || res?.code === 0 || res?.success === true) {
          showSuccessToast('删除成功')
          await this.fetchProjectList()
        } else {
          const msg = res?.message || res?.msg || res?.error || '删除失败'
          showErrorToast(msg)
        }
      } catch (error) {
        console.error('删除项目失败:', error)
        showErrorToast(error?.response?.data?.message || error?.message || '删除失败')
      } finally {
        this.deletingProjects = false
      }
    },
    previewProjectVideo(p) {
      if (!p?.shuzirenUrl) return
      this.previewVideoUrl = p.shuzirenUrl
      this.previewIsImage = false
      this.showPreviewModal = true
    },
    downloadProjectVideo(p) {
      if (!p?.shuzirenUrl) return
      const a = document.createElement('a')
      a.href = p.shuzirenUrl
      a.target = '_blank'
      a.rel = 'noopener'
      a.download = (p.title || 'video') + '.mp4'
      document.body.appendChild(a)
      a.click()
      a.remove()
    },

    // 右侧：项目列表 /itemlist
    async fetchProjectList() {
      this.loadingProjects = true
      try {
        const status =
          this.projectTab === 'generating'
            ? 0
            : this.projectTab === 'done'
              ? 1
              : 'all'
        const res = await api.get('/itemlist', { params: { status } })
        const raw = Array.isArray(res?.data?.list)
          ? res.data.list
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.list)
              ? res.list
              : Array.isArray(res)
                ? res
                : []

        this.projects = raw.map((it, idx) => ({
          id: String(it.id ?? it.item_id ?? it.itemId ?? idx),
          uuid: String(it.uuid ?? it.UUID ?? it.video_uuid ?? it.videoUuid ?? it.id ?? it.item_id ?? it.itemId ?? ''),
          title: it.text ?? it.title ?? it.name ?? it.item_name ?? '未命名项目',
          time: it.time ?? it.created_at ?? it.createdAt ?? it.create_time ?? '',
          type: it.type ?? it.category ?? it.kind ?? '项目',
          starred: !!(it.starred ?? it.is_star ?? it.fav ?? it.favorite),
          cover: it.cover ?? it.cover_url ?? it.thumb ?? it.thumbnail ?? it.image ?? '',
          shuzirenUrl: it.shuziren_url ?? it.shuzirenUrl ?? ''
        }))
      } catch (error) {
        console.error('获取项目列表失败:', error)
        this.projects = []
        showErrorToast(error?.response?.data?.message || error?.message || '加载项目列表失败')
      } finally {
        this.loadingProjects = false
      }
    },

    // 选择资产卡片
    selectVideoAsset(asset) {
      if (asset.isCreate) {
        const input = this.$refs.createVideoUploadInput
        if (input) input.click()
        return
      }
      this.selectedVideoName = asset.name
      this.selectedVideoUrl = asset.videoUrl || ''
      this.closeVideoAssetModal()
      showSuccessToast('已选择角色：' + asset.name)
    },

    // 生成视频：/shuzirencreate（需要素材视频链接 + 生成音频链接）
    async handleGenerateVideo() {
      if (this.isGeneratingVideo) return
      if (!this.selectedVideoUrl) {
        showErrorToast('请先选择一个“有视频链接”的素材/角色')
        return
      }
      if (!this.generatedAudioUrl) {
        showErrorToast('请先完成“文案转音频”生成音频')
        return
      }
      const copyText = (this.rewrittenText || '').trim()
      if (!copyText) {
        showErrorToast('请先完成文案改写')
        return
      }

      this.isGeneratingVideo = true
      try {
        const res = await api.post('/shuzirencreate', {
          video_url: this.selectedVideoUrl,
          audio_url: this.generatedAudioUrl,
          content: copyText,
          text: copyText
        })

        const videoUrl = res?.data?.video_url || res?.data?.url || res?.video_url || res?.url
        if (videoUrl) {
          this.generatedVideoUrl = videoUrl
          showSuccessToast('视频生成成功')
        } else {
          const msg =
            res?.error ||
            res?.msg ||
            res?.message ||
            res?.data?.message ||
            '视频生成已提交，请稍后在列表中查看结果'
          showSuccessToast(msg)
        }
      } catch (error) {
        console.error('视频生成失败:', error)
        const serverMsg =
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          error?.response?.data
        const status = error?.response?.status
        if (status) {
          showErrorToast(`视频生成失败(${status})：${serverMsg || error.message || '服务器错误'}`)
        } else {
          showErrorToast(error?.message || '视频生成失败，请稍后重试')
        }
      } finally {
        this.isGeneratingVideo = false
      }
    },

    // 创建：上传视频到 /video_file
    async handleCreateVideoUpload(event) {
      const file = event?.target?.files?.[0]
      event.target.value = ''
      if (!file) return
      if (!file.type.startsWith('video/')) {
        showErrorToast('请选择视频文件')
        return
      }
      this.isUploadingCreate = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        // 按你的要求：type=1 + 文件名称
        formData.append('type', '1')
        formData.append('filename', file.name)
        // 兼容可能的字段名
        formData.append('file_name', file.name)
        formData.append('name', file.name)
        const res = await api.post('/video_file', formData)
        if (res && (res.msg === 'success' || res.code === 0 || res.data)) {
          showSuccessToast('视频上传成功')
          this.fetchHistoryVideoList()
        } else {
          const msg = res?.message || res?.msg || res?.error || '视频上传失败'
          showErrorToast(msg)
        }
      } catch (error) {
        console.error('视频上传失败:', error)
        const msg = error?.response?.data?.message || error?.response?.data?.msg || error?.message || '视频上传失败'
        showErrorToast(msg)
      } finally {
        this.isUploadingCreate = false
      }
    }
  }
}
</script>

<style>
@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>

